import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, CalendarClock, MapPin, Sparkles, Scissors, Loader2, LogOut, CalendarPlus, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useClientAuth } from "../context/ClientAuth";
import { getClientBookings, cancelClientBooking, getClientNotifications, markClientNotificationsRead } from "../lib/api";
import { NotificationBell } from "../components/fyllr/NotificationBell";
import { DealAlertsCard } from "../components/fyllr/DealAlertsCard";
import { formatAppointment } from "../lib/datetime";
import { googleCalendarUrl, downloadIcs } from "../lib/calendar";
import { ClientAuthDialog } from "../components/fyllr/ClientAuthDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const money = (amt) => `$${Number(amt || 0).toFixed(0)}`;
const STATUS = {
  confirmed: "border-burgundy text-burgundy",
  reserved: "border-burgundy text-burgundy",
  authorized: "border-burgundy text-burgundy",
  paid: "border-burgundy text-burgundy",
  captured: "border-burgundy text-burgundy",
  pending: "border-sand text-mocha",
  cancelled: "border-ink/20 text-ink/40",
  refunded: "border-ink/20 text-ink/40",
  no_show: "border-ink/20 text-ink/40",
};

function BookingCard({ b, onCancel }) {
  const canCalendar = !!b.appointment_datetime;
  return (
    <article className="flex items-center gap-4 border border-sand/70 bg-bone rounded-xl p-4 md:p-5" data-testid={`my-booking-${b.id}`}>
      <div className={`shrink-0 h-12 w-12 rounded-full flex items-center justify-center ${b.type === "session" ? "bg-ink text-bone" : "bg-sand/40 text-espresso"}`}>
        {b.type === "session" ? <Sparkles size={18} /> : <Scissors size={18} />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-[10px] tracking-[0.18em] uppercase text-mocha">{b.type === "session" ? "Wellness session" : "Appointment"}</p>
          <span className={`text-[9px] tracking-[0.12em] uppercase px-2 py-0.5 rounded border ${STATUS[b.status] || STATUS.pending}`} data-testid={`my-booking-status-${b.id}`}>{b.status}</span>
        </div>
        <h3 className="mt-1 font-serif text-xl text-ink truncate" data-testid={`my-booking-service-${b.id}`}>{b.service}</h3>
        <div className="mt-1 flex items-center gap-x-4 gap-y-1 flex-wrap text-xs text-ink/55">
          <span className="inline-flex items-center gap-1.5"><MapPin size={12} className="text-mocha" />{b.salon_name}{b.suburb ? ` · ${b.suburb}` : ""}</span>
          {b.when && <span className="inline-flex items-center gap-1.5"><CalendarClock size={12} className="text-mocha" />{b.type === "opening" && b.appointment_datetime ? formatAppointment(b.appointment_datetime) : b.when}</span>}
          {b.spots ? <span>{b.spots} {b.spots === 1 ? "spot" : "spots"}</span> : null}
        </div>
        {b.is_upcoming && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {canCalendar && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="inline-flex items-center gap-1.5 rounded-full border border-sand px-3 py-1.5 text-xs text-ink/75 hover:border-espresso transition-colors" data-testid={`my-booking-calendar-${b.id}`}><CalendarPlus size={13} /> Add to calendar</button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" data-testid={`my-booking-calendar-menu-${b.id}`}>
                  <DropdownMenuItem asChild><a href={googleCalendarUrl(b)} target="_blank" rel="noreferrer" data-testid={`my-booking-gcal-${b.id}`}>Google Calendar</a></DropdownMenuItem>
                  <DropdownMenuItem onClick={() => downloadIcs(b)} data-testid={`my-booking-ical-${b.id}`}>Apple / Outlook (.ics)</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <button type="button" onClick={() => onCancel(b)} className="inline-flex items-center gap-1.5 rounded-full border border-sand px-3 py-1.5 text-xs text-ink/60 hover:border-burgundy hover:text-burgundy transition-colors" data-testid={`my-booking-cancel-${b.id}`}><X size={13} /> Cancel</button>
          </div>
        )}
      </div>
      <div className="shrink-0 text-right">
        <p className="font-serif text-xl text-espresso" data-testid={`my-booking-amount-${b.id}`}>{money(b.amount)}</p>
      </div>
    </article>
  );
}

function cancelRule(b) {
  if (!b || b.payment_mode !== "prepaid") return "Your spot will be released for others.";
  const hours = b.appointment_datetime ? (new Date(b.appointment_datetime) - Date.now()) / 3600000 : 99;
  return hours >= 2
    ? `You’re more than 2 hours out, so you’ll get a full refund of $${Number(b.amount || 0).toFixed(2)}.`
    : "Your appointment is less than 2 hours away, so this cancellation isn’t refundable — the salon keeps the full amount.";
}

export default function MyBookings() {
  const { client, loading: authLoading, logout } = useClientAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authOpen, setAuthOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const load = async () => {
    try {
      const data = await getClientBookings();
      setBookings(Array.isArray(data) ? data : []);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!client) { setAuthOpen(true); setLoading(false); return; }
    load();
  }, [client, authLoading]);

  const doCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      const res = await cancelClientBooking(cancelTarget.id);
      if (res?.outcome === "full_refund" && res?.refund_status === "authorization_released") toast.success("Booking cancelled. Your card hold has been released — you won’t be charged.");
      else if (res?.outcome === "full_refund") toast.success(`Booking cancelled. $${Number(res.refund_amount || 0).toFixed(2)} is on its way back to your card.`);
      else if (res?.outcome === "salon_keeps_full_amount") toast.success("Booking cancelled. As this was within 2 hours of your appointment, it isn’t refundable.");
      else toast.success("Booking cancelled.");
      setCancelTarget(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel this booking.");
    } finally { setCancelling(false); }
  };

  if (authLoading) return <div className="min-h-screen bg-bone pt-14 md:pt-16" data-testid="my-bookings-loading" />;

  if (!client) {
    return (
      <div className="min-h-screen bg-bone pt-14 md:pt-16 flex items-center" data-testid="my-bookings-signin-gate">
        <div className="max-w-md mx-auto px-6 text-center">
          <p className="text-[10px] tracking-[0.3em] uppercase text-mocha">Your account</p>
          <h1 className="mt-4 font-serif text-4xl text-ink">Sign in to see your bookings.</h1>
          <p className="mt-3 text-sm text-ink/55">Your last-minute appointments and wellness sessions live here.</p>
          <button type="button" onClick={() => setAuthOpen(true)} className="mt-6 rounded-full bg-espresso text-bone px-6 py-3 text-sm hover:bg-ink transition-colors" data-testid="my-bookings-signin-btn">Sign in</button>
        </div>
        <ClientAuthDialog open={authOpen} onClose={() => navigate("/marketplace")} onSuccess={() => setAuthOpen(false)} reason="Sign in to view your bookings." />
      </div>
    );
  }

  const upcoming = bookings.filter((b) => b.is_upcoming);
  const past = bookings.filter((b) => !b.is_upcoming);

  return (
    <div className="min-h-screen bg-bone pt-14 md:pt-16 pb-20" data-testid="my-bookings-page">
      <div className="max-w-[1000px] mx-auto px-5 md:px-8 py-10 md:py-14">
        <div className="flex items-start justify-between gap-5 flex-wrap">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-mocha" data-testid="my-bookings-eyebrow">Your account</p>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-ink leading-[0.95]" data-testid="my-bookings-heading">Your bookings.</h1>
            <p className="mt-3 text-sm text-ink/60" data-testid="my-bookings-greeting">Signed in as {client.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell fetchFeed={getClientNotifications} markRead={markClientNotificationsRead} testId="client-notifications" />
            <button type="button" onClick={async () => { await logout(); navigate("/marketplace"); }} className="inline-flex items-center gap-2 border border-sand rounded-full px-4 py-2 text-xs text-ink/70 hover:border-espresso transition-colors" data-testid="my-bookings-logout"><LogOut size={13} /> Sign out</button>
          </div>
        </div>

        <DealAlertsCard />

        {loading ? (
          <div className="flex items-center justify-center py-20 text-ink/50" data-testid="my-bookings-loading-list"><Loader2 className="animate-spin mr-2" size={18} /> Loading your bookings…</div>
        ) : bookings.length === 0 ? (
          <div className="mt-10 border border-sand/70 rounded-xl py-16 px-6 text-center" data-testid="my-bookings-empty">
            <p className="font-serif text-2xl text-ink">No bookings yet.</p>
            <p className="mt-2 text-sm text-ink/55">Your first last-minute find is one tap away.</p>
            <Link to="/marketplace" className="mt-6 inline-flex items-center gap-2 rounded-full bg-espresso text-bone px-5 py-2.5 text-sm hover:bg-ink transition-colors" data-testid="my-bookings-browse">Browse the marketplace <ArrowRight size={14} /></Link>
          </div>
        ) : (
          <>
            <section className="mt-10" data-testid="my-bookings-upcoming">
              <div className="flex items-center justify-between gap-3 mb-4">
                <h2 className="font-serif text-2xl text-ink">Upcoming</h2>
                <Link to="/marketplace" className="inline-flex items-center gap-1.5 text-sm text-burgundy hover:text-espresso transition-colors" data-testid="my-bookings-rebook"><RotateCcw size={13} /> Book again</Link>
              </div>
              {upcoming.length === 0 ? (
                <p className="text-sm text-ink/45 border border-dashed border-sand/70 rounded-xl py-8 text-center" data-testid="my-bookings-upcoming-empty">Nothing booked ahead. <Link to="/marketplace" className="text-burgundy underline underline-offset-2">Find a last-minute slot →</Link></p>
              ) : (
                <div className="space-y-3">{upcoming.map((b) => <BookingCard key={b.id} b={b} onCancel={setCancelTarget} />)}</div>
              )}
            </section>

            {past.length > 0 && (
              <section className="mt-12" data-testid="my-bookings-past">
                <h2 className="font-serif text-2xl text-ink mb-4">Past &amp; cancelled</h2>
                <div className="space-y-3 opacity-80">{past.map((b) => <BookingCard key={b.id} b={b} onCancel={setCancelTarget} />)}</div>
              </section>
            )}
          </>
        )}
      </div>

      <AlertDialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <AlertDialogContent data-testid="my-bookings-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this booking?</AlertDialogTitle>
            <AlertDialogDescription>
              {cancelTarget ? `${cancelTarget.service} at ${cancelTarget.salon_name}. ` : ""}<span data-testid="my-bookings-cancel-rule">{cancelRule(cancelTarget)}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="my-bookings-cancel-keep">Keep booking</AlertDialogCancel>
            <AlertDialogAction onClick={doCancel} disabled={cancelling} className="bg-burgundy hover:bg-espresso" data-testid="my-bookings-cancel-confirm">
              {cancelling ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null} Yes, cancel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
